import { createApp, h } from 'vue';

/**
 * Global widget registry
 * Each widget registers itself here for mounting by WordPress
 */
window.CustomElementorWidgets = window.CustomElementorWidgets || {};

/**
 * Store mounted Vue app instances by element ID for cleanup
 * This prevents memory leaks when Elementor re-renders widgets
 */
const mountedApps = new Map();

/**
 * Register a Vue component as an Elementor widget
 *
 * @param {string} widgetId - The widget ID (must match PHP get_widget_id())
 * @param {Object} component - The Vue component to register
 */
export function registerWidget(widgetId, component) {
  window.CustomElementorWidgets[widgetId] = {
    component,
    mount(selector) {
      const el = document.querySelector(selector);
      if (!el) {
        console.warn(`[CustomElementorWidgets] Element not found: ${selector}`);
        return null;
      }

      // Unmount existing app if present (prevents memory leaks on re-render)
      if (mountedApps.has(selector)) {
        try {
          mountedApps.get(selector).unmount();
        } catch (e) {
          // Ignore unmount errors
        }
        mountedApps.delete(selector);
      }

      // Parse settings from data attribute
      let settings = {};
      try {
        const settingsAttr = el.getAttribute('data-settings');
        if (settingsAttr) {
          settings = JSON.parse(settingsAttr);
        }
      } catch (e) {
        console.error(`[CustomElementorWidgets] Failed to parse settings for ${widgetId}:`, e);
      }

      // Create and mount Vue app
      const app = createApp({
        render() {
          return h(component, { settings });
        },
      });

      app.mount(el);

      // Store reference for cleanup
      mountedApps.set(selector, app);

      return app;
    },
  };
}

/**
 * Utility to mount a widget for Storybook or testing
 *
 * @param {Object} component - The Vue component
 * @param {Object} settings - Widget settings object
 * @returns {Object} - Vue component with props
 */
export function createWidgetWrapper(component, settings = {}) {
  return {
    components: { WidgetComponent: component },
    setup() {
      return { settings };
    },
    template: '<WidgetComponent :settings="settings" />',
  };
}
